import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  AnimatePresence,
  motion,
} from "framer-motion";

import CardPicker from "./CardPicker";
import LoadingSpinner from "./LoadingSpinner";
import { getSkinSrc } from "./SeasonalSkins";

import "../index.css";


const API_BASE =
  process.env.REACT_APP_API_URL || "";

const BOT_USERNAME =
  String(
    process.env.REACT_APP_TELEGRAM_BOT_USERNAME || ""
  )
    .replace(/^@/, "")
    .trim();


const CURRENCY = {
  sparks: {
    label: "Sparks",
    icon: "/sparks.png",
  },

  runes: {
    label: "Runes",
    icon: "/runes.png",
  },
};


const STAKE_OPTIONS = {
  sparks: [
    100,
    500,
    1000,
    5000,
    10000,
    25000,
  ],

  runes: [
    2,
    10,
    50,
    100,
    500,
    1000,
  ],
};


const STAKE_LIMITS = {
  sparks: {
    min: 100,
    max: 10000000,
  },

  runes: {
    min: 2,
    max: 1000000,
  },
};


const DEFAULT_NORMAL_MULTIPLIER =
  1.5;


/*
 * QUANTIDADE DE CHALLENGES POR PÁGINA.
 */
const PAGE_SIZE = 10;
const CHALLENGE_ACCEPTED_REFUND_MS = 5 * 60 * 1000;


function fmt(value) {
  return Number(
    value || 0
  ).toLocaleString();
}


function timeLeft(
  expiresAt,
  now = Date.now()
) {
  const end =
    new Date(
      expiresAt || 0
    ).getTime();

  if (
    !Number.isFinite(end)
  ) {
    return "";
  }

  const sec =
    Math.max(
      0,
      Math.floor(
        (end - now) / 1000
      )
    );

  if (
    sec <= 0
  ) {
    return "Expired";
  }

  const h =
    Math.floor(
      sec / 3600
    );

  const m =
    Math.floor(
      (sec % 3600) / 60
    );

  const s =
    sec % 60;

  if (
    h > 0
  ) {
    return `${h}h ${String(m).padStart(2, "0")}m`;
  }

  return `${m}:${String(s).padStart(2, "0")}`;
}


function isChallengeExpired(
  challenge,
  now = Date.now()
) {
  const expiresAt =
    new Date(
      challenge?.expiresAt || 0
    ).getTime();

  return (
    Number.isFinite(
      expiresAt
    ) &&
    expiresAt <= now
  );
}


function capitalizeName(value) {
  const text =
    String(
      value || ""
    ).trim();

  if (!text) {
    return "Player";
  }

  return (
    text.charAt(0).toUpperCase() +
    text.slice(1)
  );
}


function getChallengeResult(
  challenge
) {
  if (
    !challenge ||
    String(
      challenge.status || ""
    ).toLowerCase() !==
      "settled"
  ) {
    return null;
  }

  const winnerUserId =
    challenge?.winnerUserId;

  if (!winnerUserId) {
    return null;
  }

  let viewerUserId =
    null;

  if (
    challenge?.isCreator
  ) {
    viewerUserId =
      challenge
        ?.creator
        ?.userId;

  } else if (
    challenge?.isAcceptor
  ) {
    viewerUserId =
      challenge
        ?.acceptor
        ?.userId;
  }

  if (!viewerUserId) {
    return null;
  }

  return (
    String(
      winnerUserId
    ) ===
    String(
      viewerUserId
    )
  )
    ? "win"
    : "loss";
}


function getChallengeWinnerName(
  challenge
) {
  if (
    !challenge?.winnerUserId
  ) {
    return null;
  }

  if (
    String(
      challenge.winnerUserId
    ) ===
    String(
      challenge
        ?.creator
        ?.userId
    )
  ) {
    return capitalizeName(
      challenge
        ?.creator
        ?.name ||
      "Creator"
    );
  }

  if (
    String(
      challenge.winnerUserId
    ) ===
    String(
      challenge
        ?.acceptor
        ?.userId
    )
  ) {
    return capitalizeName(
      challenge
        ?.acceptor
        ?.name ||
      "Opponent"
    );
  }

  return "Player";
}


function getChallengeNormalMultiplier(
  challenge
) {
  const value =
    Number(
      challenge?.normalMultiplier
    );

  if (
    Number.isFinite(value) &&
    value > 1
  ) {
    return Math.min(
      2,
      value
    );
  }

  return DEFAULT_NORMAL_MULTIPLIER;
}


function getChallengeWinnerPayout(
  challenge
) {
  const stake =
    Math.max(
      0,
      Math.floor(
        Number(
          challenge?.amount || 0
        )
      )
    );

  if (!stake) {
    return 0;
  }

  const direct =
    Number(
      challenge?.winnerPayout
    );

  if (
    String(
      challenge?.status || ""
    ).toLowerCase() ===
      "settled" &&
    Number.isFinite(
      direct
    ) &&
    direct > stake
  ) {
    return Math.min(
      stake * 2,
      Math.floor(
        direct
      )
    );
  }

  const normalDirect =
    Number(
      challenge?.normalWinnerPayout
    );

  if (
    Number.isFinite(
      normalDirect
    ) &&
    normalDirect > stake
  ) {
    return Math.min(
      stake * 2,
      Math.floor(
        normalDirect
      )
    );
  }

  const multiplier =
    getChallengeNormalMultiplier(
      challenge
    );

  return Math.min(
    stake * 2,
    Math.floor(
      stake *
      multiplier
    )
  );
}


function readSelectedCardSnapshot() {
  try {
    const raw =
      localStorage.getItem(
        "selectedCardSnapshot"
      );

    if (!raw) {
      return null;
    }

    const parsed =
      JSON.parse(raw);

    return (
      parsed &&
      typeof parsed === "object"
    )
      ? parsed
      : null;

  } catch {
    return null;
  }
}


function cardLayers(card) {
  const character =
    card?.character;

  if (
    !character ||
    typeof character !== "object"
  ) {
    return [];
  }

  const order = [
    "background",
    "body",
    "boots",
    "pants",
    "shirt",
    "neck",
    "face",
    "eyes",
    "mouth",
    "head",
    "weapons",
  ];

  const seasonName =
    card
      ?.seasonal_accessory
      ?.season_name ||
    card?.season_name ||
    null;

  return order
    .map(
      (
        part,
        idx
      ) => {
        const data =
          character?.[part];

        if (
          !data ||
          typeof data !==
            "object" ||
          !data.side ||
          !data.rarity ||
          !data.variant
        ) {
          return null;
        }

        const defaultSrc =
          `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

        return {
          part,

          src:
            getSkinSrc(
              seasonName,
              part,
              defaultSrc
            ),

          zIndex:
            idx + 1,
        };
      }
    )
    .filter(Boolean);
}


function FighterThumb({
  card,
  size = 62,
}) {
  const layers =
    cardLayers(card);

  const rarity =
    String(
      card?.rarity ||
      "Common"
    ).toLowerCase();

  const fallback =
    `/${rarity}.png`;

  return (
    <div
      style={{
        width:
          size,

        height:
          size,

        flex:
          `0 0 ${size}px`,

        position:
          "relative",

        overflow:
          "hidden",

        borderRadius:
          10,

        background:
          "#1a0101",

        border:
          "1px solid rgba(255,255,255,.16)",
      }}
    >
      {layers.length ? (
        layers.map(
          (
            layer,
            idx
          ) => (
            <img
              key={
                `${layer.part || "layer"}-${idx}`
              }

              src={
                layer.src
              }

              alt=""

              draggable={
                false
              }

              style={{
                position:
                  "absolute",

                inset:
                  0,

                width:
                  "100%",

                height:
                  "100%",

                objectFit:
                  "cover",

                zIndex:
                  layer.zIndex ??
                  idx + 1,
              }}
            />
          )
        )
      ) : (
        <img
          src={
            fallback
          }

          alt=""

          style={{
            width:
              "100%",

            height:
              "100%",

            objectFit:
              "contain",

            padding:
              8,

            boxSizing:
              "border-box",
          }}
        />
      )}
    </div>
  );
}


function CurrencyAmount({
  currency,
  amount,
  prefix = "",
}) {
  const meta =
    CURRENCY[currency] ||
    CURRENCY.sparks;

  return (
    <span
      style={{
  display: "inline-flex",

  alignItems: "center",

  gap: 2,

  maxWidth: "100%",
  minWidth: 0,

  color: "#f3e14d",

  fontSize: 15,

  fontWeight: 600,

  lineHeight: "18px",

  whiteSpace: "nowrap",
}}
    >
      <img
        src={
          meta.icon
        }

        alt=""

        style={{
          width:
            18,

          height:
            18,

          objectFit:
            "contain",
        }}
      />

      {prefix}

      {fmt(
        amount
      )}

      {" "}

      {meta.label}
    </span>
  );
}


function RarityBadge({
  rarity
}) {
  const value =
    String(
      rarity ||
      "Common"
    ).toUpperCase();

  return (
    <span
      style={{
        display:
          "inline-flex",

        alignItems:
          "center",

        flex:
          "0 0 auto",

        

        borderRadius:
          5,

        background:
          "rgba(0,0,0,.38)",

        border:
          "1px solid rgba(255,255,255,.17)",

        color:
          "#ffe675",


minHeight: 16,
padding: "1px 5px",
fontSize: 9,
lineHeight: "11px",

        fontWeight:
          900,

        textTransform:
          "uppercase",

        boxSizing:
          "border-box",
      }}
    >
      {value}
    </span>
  );
}


function ChallengeRow({
  challenge,
  now,
  onAccept,
  onShare,
  onCancel,
  onRefund,
  onOpen,
}) {
  const creatorCard =
    challenge
      ?.creator
      ?.card || {};

  const status =
    String(
      challenge?.status ||
      "open"
    ).toLowerCase();

  const mine =
    !!challenge
      ?.isCreator;

  const creatorName =
    capitalizeName(
      challenge
        ?.creator
        ?.name
    );

  const acceptorName =
    challenge
      ?.acceptor
      ?.name
      ? capitalizeName(
          challenge
            .acceptor
            .name
        )
      : null;

  const battleTitle =
    acceptorName
      ? `${creatorName} vs ${acceptorName}`
      : creatorName;

  const expired =
    isChallengeExpired(
      challenge,
      now
    );

  const acceptedAt =
  new Date(
    challenge?.acceptedAt || 0
  ).getTime();

const acceptedRefundReady =
  status === "accepted" &&
  Number.isFinite(
    acceptedAt
  ) &&
  now - acceptedAt >=
    CHALLENGE_ACCEPTED_REFUND_MS;

const canRefund =
  mine &&
  (
    (
      status === "open" &&
      expired
    ) ||
    acceptedRefundReady
  );

  const result =
    getChallengeResult(
      challenge
    );

  const winnerPayout =
    getChallengeWinnerPayout(
      challenge
    );

  return (
    <div
      className="task-card"

      style={{
        minHeight:
          92,

        height:
          "auto",

        display:
          "flex",

        alignItems:
          "flex-start",

        gap:
          10,

          position: "relative",
width: "100%",

 float: "none",

        padding:
          "9px",

        boxSizing:
          "border-box",


        cursor:
          "pointer",
      }}

      onClick={() =>
        onOpen?.(
          challenge
        )
      }
    >
     <div
  style={{
    position: "relative",
    flex: "0 0 auto",
  }}
>
  <FighterThumb
    card={
      creatorCard
    }

    size={
      68
    }
  />

  <div
  style={{
    position: "absolute",

    left: "50%",
    bottom: 3,

    transform: "translateX(-50%)",

    zIndex: 50,

    pointerEvents: "none",
  }}
>
    <RarityBadge
      rarity={
        creatorCard
          ?.rarity
      }
    />
  </div>
</div>


      <div
        style={{
          minWidth:
            0,

          flex:
            1,

          textAlign:
            "left",
        }}
      >
        <div
          style={{
            fontSize:
              16,

            fontWeight:
              800,

            color:
              "#fff",

            whiteSpace:
              "nowrap",

            overflow:
              "hidden",

            textOverflow:
              "ellipsis",

            textShadow:
              "1px 1px #000",
          }}
        >
          {battleTitle}
        </div>


       <div
  style={{
    marginTop: 4,

    color: "#fff",

    fontSize: 13,

    fontWeight: 700,

    whiteSpace: "nowrap",

    overflow: "hidden",

    textOverflow: "ellipsis",
  }}
>
  {creatorCard?.name ||
    "Fighter"}
</div>


       <div
  style={{
    marginTop: 5,

    display: "flex",
    alignItems: "center",
    gap: 6,

    minHeight: 22,

    fontSize: 14,
  }}
>
  <CurrencyAmount
    currency={
      challenge?.currency
    }

    amount={
      challenge?.amount
    }
  />
</div>


        {result ? (
  <div
    style={{
      marginTop: 0,

      display: "flex",
      alignItems: "center",
      gap: 7,

      minHeight: 22,

      fontSize: 13,
      fontWeight: 600,

      color:
        result === "win"
          ? "#aaff72"
          : "#ff8b7b",

      textShadow:
        "1px 1px #000",
    }}
  >
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        lineHeight: "18px",
        flex: "0 0 auto",
      }}
    >
      {result === "win"
        ? "WON"
        : "LOST"}
    </span>

    {result === "win" && (
      <>
        <CurrencyAmount
          currency={
            challenge?.currency
          }
          amount={
            winnerPayout
          }
        />
      </>
    )}
  </div>
) : (
          <div
            style={{
              marginTop:
                4,

              fontSize:
                12,

              fontWeight:
                expired
                  ? 800
                  : 400,

              color:
                expired
                  ? "#ffb06b"
                  : "rgba(255,255,255,.62)",
            }}
          >
            {status === "open"
  ? expired
    ? (
        mine
          ? "REFUND AVAILABLE"
          : "EXPIRED"
      )
    : `${timeLeft(
        challenge
          ?.expiresAt,
        now
      )} left`

  : status === "accepted" &&
    acceptedRefundReady
    ? "ABANDONED"

  : status.toUpperCase()
}

            {!expired &&
              status ===
                "open" &&
              challenge
                ?.visibility ===
                "private"
              ? " · SHARE"
              : ""
            }
          </div>
        )}
      </div>


      <div
  style={{
    display: "flex",
    flexDirection: "column",

    gap: 8,

    flex: "0 0 auto",

    alignSelf: "center",
  }}
>
        {status ===
          "open" &&
          !expired &&
          challenge
            ?.canAccept && (
            <button
              type="button" style={{
    margin: 0,
  }}

              className=
                "game-button-small green-button"

              onClick={
                e => {
                  e.stopPropagation();

                  onAccept?.(
                    challenge
                  );
                }
              }
            >
              ACCEPT
            </button>
          )
        }


        {status ===
          "open" &&
          mine &&
          !expired && (
            <button
              type="button"

              style={{
    margin: 0,
  }}

              className=
                "game-button-small red-button"

              onClick={
                e => {
                  e.stopPropagation();

                  onCancel?.(
                    challenge
                  );
                }
              }
            >
              CANCEL
            </button>
          )
        }


        {canRefund && (
          <button
            type="button"

            style={{
    margin: 0,
  }}

            className=
              "game-button-small green-button"

            onClick={
              e => {
                e.stopPropagation();

                onRefund?.(
                  challenge
                );
              }
            }
          >
            REFUND
          </button>
        )}


        {status ===
          "open" &&
          !expired && (
            <button
              type="button"

              style={{
    margin: 0,
  }}

              className=
                "game-button-small yellow-button"

              onClick={
                e => {
                  e.stopPropagation();

                  onShare?.(
                    challenge
                  );
                }
              }
            >
              SHARE
            </button>
          )
        }
      </div>
    </div>
  );
}


function Section({
  title,
  children,
  right,
}) {
  return (
    <section
      style={{
        width: "calc(100% + 20px)",
        minWidth: 0,
        maxWidth: "calc(100% + 20px)",

        marginLeft: -10,
        marginRight: -10,
        marginBottom: 0,

        boxSizing: "border-box",
        position: "relative",

        fontFamily: '"Fredoka", sans-serif',
      }}
    >
      <div
        className="modal_title tasks_modal_title"
        style={{
          width: "100%",
          minWidth: 0,

          textAlign: "center",

          paddingLeft: 0,
          paddingRight: 0,

          boxSizing: "border-box",

          marginBottom: -4,

          fontFamily: '"Fredoka", sans-serif',
          fontSize: 21,
          fontWeight: 600,
        }}
      >
        {title}

        {right}
      </div>


      <div
        style={{
          width: "calc(100% - 30px)",
          maxWidth: "100%",
          margin: 15,
          marginTop: -15,
          boxSizing: "border-box",
          padding: 10,
          paddingTop: 25,
          position: "relative",

          fontFamily: '"Fredoka", sans-serif',
          fontSize: 15,
          backgroundColor: "#a66f40",
          borderBottom: "5px solid #996c3e",
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
        }}
      >
        {children}
      </div>
    </section>
  );
}


function Empty({
  children
}) {
  return (
    <div
      className="shadow1"

      style={{
        color:
          "#fff",

        textAlign:
          "center",

        padding:
          "14px 10px",

        fontSize:
          15,
      }}
    >
      {children}
    </div>
  );
}


function Pagination({
  page,
  hasMore,
  loading,
  onPrevious,
  onNext,
}) {
  if (
    page <= 1 &&
    !hasMore
  ) {
    return null;
  }

  return (
   <div
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    gap: 6,

    width: "100%",

    paddingTop: 8,
    paddingBottom: 1
  }}
>
      <button
        type="button"

        disabled={
          page <= 1 ||
          loading
        }

        onClick={
          onPrevious
        }

        style={{
  width: 46,
  height: 46,

  border: 0,
  padding: 0,
  background: "transparent",

  opacity:
    page <= 1 || loading
      ? 0.45
      : 1,

  cursor:
    page <= 1 || loading
      ? "not-allowed"
      : "pointer",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}}
      >
        <img
          src="/v2/left.png"

          alt="Previous"

          draggable={
            false
          }

          style={{
  width: 44,
  height: 44,
  objectFit: "contain",
  display: "block",
  pointerEvents: "none",
}}
        />
      </button>


      <div
        style={{
          height:
            42,

          lineHeight:
            "38px",

          borderRadius:
            8,

          background:
            "rgba(0,0,0,0.45)",

          border:
            "2px solid #7f5031",

          fontFamily:
            '"Fredoka", sans-serif',

          padding:
            "0 15px",

          color:
            "#fff",

          fontSize:
            14,

          fontWeight:
            600,

          textAlign:
            "center",

          textShadow:
            "1px 1px #000",

          boxSizing:
            "border-box",

          whiteSpace:
            "nowrap",
        }}
      >
        Page{" "}

        <span
          style={{
            color:
              "#ffdf5f",
          }}
        >
          {page}
        </span>
      </div>


      <button
        type="button"

        disabled={
          !hasMore ||
          loading
        }

        onClick={
          onNext
        }

       style={{
  width: 46,
  height: 46,

  border: 0,
  padding: 0,
  background: "transparent",

    opacity:
    !hasMore || loading
      ? 0.45
      : 1,

  cursor:
    page <= 1 || loading
      ? "not-allowed"
      : "pointer",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}}
      >
        <img
          src="/v2/right.png"

          alt="Next"

          draggable={
            false
          }

          style={{
  width: 44,
  height: 44,
  objectFit: "contain",
  display: "block",
  pointerEvents: "none",
}}
        />
      </button>
    </div>
  );
}

function ChallengeFightPickerModal({
  open,
  user,
  mode,
  challenge,
  onClose,
  onSelectDone,
  onNavigate,
}) {
  return (
    <div>
      {open && (
        <div
          className="modal_box"
          style={{
            zIndex: 9999,
          }}
        >
          <div className="modal_title">
            {mode === "accept"
              ? `Choose ${
                  challenge?.creator?.card?.rarity ||
                  "Matching"
                } Fighter`
              : "Choose Your Fighter"
            }

            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="modal_close"
            />
          </div>

          <div>
            <CardPicker
              user={user}

              onClose={onClose}

              onNavigate={onNavigate}

              openedVia="challenge"

              initialRarity={
                mode === "accept"
                  ? challenge
                      ?.creator
                      ?.card
                      ?.rarity
                  : "all"
              }

              onSelected={(params) => {
                onSelectDone?.(
                  params
                );
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function ChallengePage({
  user,
  setUser,
  onNavigate,
  initialChallengeId = null,
  hasBattlePass = false,
  battlePassLoading = false,
}) {
  const [
    view,
    setView
  ] =
    useState(
      initialChallengeId
        ? "detail"
        : "browse"
    );


  const [
    publicList,
    setPublicList
  ] =
    useState([]);


  const [
    mineList,
    setMineList
  ] =
    useState([]);


  /*
   * PAGINAÇÃO OPEN
   */
  const [
    publicPage,
    setPublicPage
  ] =
    useState(1);

  const [
    publicCursor,
    setPublicCursor
  ] =
    useState(null);

  const [
    publicHasMore,
    setPublicHasMore
  ] =
    useState(false);

  const [
    publicHistory,
    setPublicHistory
  ] =
    useState([]);


  /*
   * PAGINAÇÃO MY CHALLENGES
   */
  const [
    minePage,
    setMinePage
  ] =
    useState(1);

  const [
    mineCursor,
    setMineCursor
  ] =
    useState(null);

  const [
    mineHasMore,
    setMineHasMore
  ] =
    useState(false);

  const [
    mineHistory,
    setMineHistory
  ] =
    useState([]);


  const [
    pageLoading,
    setPageLoading
  ] =
    useState(false);


  const [
    detail,
    setDetail
  ] =
    useState(null);


  const [
    loading,
    setLoading
  ] =
    useState(true);


  const [
    busy,
    setBusy
  ] =
    useState(false);


  /*
   * Ainda usado para erros de carregamento da página/listas.
   */
  const [
    error,
    setError
  ] =
    useState("");


  /*
   * SUCCESS MODAL
   */
  const [
    successModalOpen,
    setSuccessModalOpen
  ] =
    useState(false);

  const [
    successModalMessage,
    setSuccessModalMessage
  ] =
    useState("");


  const [
    now,
    setNow
  ] =
    useState(
      Date.now()
    );


  /*
   * ERROR MODAL
   */
  const [
    errorModalOpen,
    setErrorModalOpen
  ] =
    useState(false);

  const [
    errorModalMessage,
    setErrorModalMessage
  ] =
    useState("");


  const [
    createCard,
    setCreateCard
  ] =
    useState(null);


  const [
    currency,
    setCurrency
  ] =
    useState(
      "sparks"
    );


  const [
    amount,
    setAmount
  ] =
    useState(
      "1000"
    );


  const [
    visibility,
    setVisibility
  ] =
    useState(
      "public"
    );


  const [
    pickerMode,
    setPickerMode
  ] =
    useState(null);


  const [
    acceptChallenge,
    setAcceptChallenge
  ] =
    useState(null);


 


  const token =
    localStorage.getItem(
      "token"
    );


  const authHeaders =
    useCallback(
      (
        json = false
      ) => ({
        ...(json
          ? {
              "Content-Type":
                "application/json",
            }
          : {}),

        Authorization:
          `Bearer ${token}`,

        "X-TG-INITDATA":
          window
            .Telegram
            ?.WebApp
            ?.initData ||
          "",
      }),

      [
        token
      ]
    );


  const showErrorModal =
    useCallback(
      (
        code,
        fallback =
          "Something went wrong."
      ) => {
        const messages = {
          CARD_ALREADY_IN_CHALLENGE:
            "This fighter is already being used in another active challenge.",

          TOO_MANY_OPEN_CHALLENGES:
            "You can have a maximum of 3 open challenges at the same time. Complete or cancel one before creating another.",

          INSUFFICIENT_RUNES:
            "You don't have enough Runes.",

          INSUFFICIENT_SPARKS:
            "You don't have enough Sparks.",

          CHALLENGE_NOT_FOUND:
            "This challenge is no longer available.",

          CHALLENGE_NOT_OPEN:
            "This challenge is no longer open.",

          CHALLENGE_EXPIRED:
            "This challenge has expired.",

          CANNOT_ACCEPT_OWN_CHALLENGE:
            "You cannot accept your own challenge.",

          WRONG_RARITY:
            "You need to use a fighter with the same rarity.",
        };

        setErrorModalMessage(
          messages[code] ||
          fallback
        );

        setErrorModalOpen(
          true
        );
      },

      []
    );


  const showSuccessModal =
    useCallback(
      (
        message
      ) => {
        setSuccessModalMessage(
          message ||
          "Done!"
        );

        setSuccessModalOpen(
          true
        );
      },

      []
    );


  useEffect(() => {
    const id =
      setInterval(
        () =>
          setNow(
            Date.now()
          ),

        1000
      );

    return () =>
      clearInterval(
        id
      );
  }, []);


  /*
   * CARREGA PRIMEIRA PÁGINA
   */
  const loadLists =
    useCallback(
      async () => {
        setLoading(
          true
        );

        setError(
          ""
        );

        try {
          const [
            pubRes,
            mineRes,
          ] =
            await Promise.all([
              fetch(
                `${API_BASE}/api/battle/challenges?limit=${PAGE_SIZE}`,

                {
                  headers:
                    authHeaders(),
                }
              ),

              fetch(
                `${API_BASE}/api/battle/challenges?mine=1&limit=${PAGE_SIZE}`,

                {
                  headers:
                    authHeaders(),
                }
              ),
            ]);


          const [
            pubData,
            mineData,
          ] =
            await Promise.all([
              pubRes
                .json()
                .catch(
                  () => ({})
                ),

              mineRes
                .json()
                .catch(
                  () => ({})
                ),
            ]);


          if (
            !pubRes.ok
          ) {
            throw new Error(
              pubData?.error ||
              "Failed to load challenges"
            );
          }


          if (
            !mineRes.ok
          ) {
            throw new Error(
              mineData?.error ||
              "Failed to load your challenges"
            );
          }


          setPublicList(
            Array.isArray(
              pubData?.challenges
            )
              ? pubData.challenges
              : []
          );


          setMineList(
            Array.isArray(
              mineData?.challenges
            )
              ? mineData.challenges
              : []
          );


          setPublicCursor(
            pubData
              ?.pagination
              ?.nextCursor ||
            null
          );


          setMineCursor(
            mineData
              ?.pagination
              ?.nextCursor ||
            null
          );


          setPublicHasMore(
            !!pubData
              ?.pagination
              ?.hasMore
          );


          setMineHasMore(
            !!mineData
              ?.pagination
              ?.hasMore
          );


          setPublicPage(
            1
          );

          setMinePage(
            1
          );


          setPublicHistory(
            []
          );

          setMineHistory(
            []
          );

        } catch (e) {
          setError(
            e?.message ||
            "Failed to load challenges"
          );

        } finally {
          setLoading(
            false
          );
        }
      },

      [
        authHeaders
      ]
    );


  /*
   * CARREGA DETAIL
   */
  const loadDetail =
    useCallback(
      async (
        challengeId
      ) => {
        if (
          !challengeId
        ) {
          return;
        }

        setLoading(
          true
        );

        setError(
          ""
        );

        try {
          const res =
            await fetch(
              `${API_BASE}/api/battle/challenges/${encodeURIComponent(
                challengeId
              )}`,

              {
                headers:
                  authHeaders(),
              }
            );


          const data =
            await res
              .json()
              .catch(
                () => ({})
              );


          if (
            !res.ok
          ) {
            throw new Error(
              data?.error ||
              "Could not load challenge"
            );
          }


          setDetail(
            data?.challenge ||
            null
          );

          setView(
            "detail"
          );

        } catch (e) {
          setError(
            e?.message ||
            "Could not load challenge"
          );

        } finally {
          setLoading(
            false
          );
        }
      },

      [
        authHeaders
      ]
    );


  useEffect(() => {
    if (
      initialChallengeId
    ) {
      loadDetail(
        initialChallengeId
      );

    } else {
      loadLists();
    }
  }, [
    initialChallengeId,
    loadDetail,
    loadLists,
  ]);


  /*
   * PAGINAÇÃO
   *
   * SUBSTITUI A LISTA.
   * NÃO FAZ APPEND.
   */
  const loadChallengePage =
    useCallback(
      async (
        type,
        direction
      ) => {
        if (
          pageLoading
        ) {
          return;
        }


        const mine =
          type ===
          "mine";


        const currentPage =
          mine
            ? minePage
            : publicPage;


        const currentCursor =
          mine
            ? mineCursor
            : publicCursor;


        const history =
          mine
            ? mineHistory
            : publicHistory;


        if (
          direction ===
            "next" &&
          (
            !currentCursor?.createdAt ||
            !currentCursor?.id
          )
        ) {
          return;
        }


        if (
          direction ===
            "prev" &&
          currentPage <= 1
        ) {
          return;
        }


        setPageLoading(
          true
        );

        setError(
          ""
        );


        try {
          const params =
            new URLSearchParams();


          params.set(
            "limit",
            String(
              PAGE_SIZE
            )
          );


          if (
            mine
          ) {
            params.set(
              "mine",
              "1"
            );
          }


          let requestCursor =
            null;


          if (
            direction ===
            "next"
          ) {
            requestCursor =
              currentCursor;
          }


          if (
            direction ===
            "prev"
          ) {
            requestCursor =
              currentPage > 2
                ? history[
                    currentPage -
                    3
                  ]
                : null;
          }


          if (
            requestCursor
              ?.createdAt &&
            requestCursor
              ?.id
          ) {
            params.set(
              "cursorCreatedAt",
              requestCursor
                .createdAt
            );

            params.set(
              "cursorId",
              requestCursor
                .id
            );
          }


          const res =
            await fetch(
              `${API_BASE}/api/battle/challenges?${params.toString()}`,

              {
                headers:
                  authHeaders(),
              }
            );


          const data =
            await res
              .json()
              .catch(
                () => ({})
              );


          if (
            !res.ok
          ) {
            throw new Error(
              data?.error ||
              "Failed to load challenges"
            );
          }


          const nextList =
            Array.isArray(
              data?.challenges
            )
              ? data.challenges
              : [];


          const nextCursor =
            data
              ?.pagination
              ?.nextCursor ||
            null;


          const hasMore =
            !!data
              ?.pagination
              ?.hasMore;


          /*
           * MY CHALLENGES
           */
          if (
            mine
          ) {
            setMineList(
              nextList
            );

            setMineCursor(
              nextCursor
            );

            setMineHasMore(
              hasMore
            );


            if (
              direction ===
              "next"
            ) {
              setMineHistory(
                previous => [
                  ...previous,
                  currentCursor,
                ]
              );

              setMinePage(
                previous =>
                  previous + 1
              );

            } else {
              setMineHistory(
                previous =>
                  previous.slice(
                    0,
                    -1
                  )
              );

              setMinePage(
                previous =>
                  Math.max(
                    1,
                    previous - 1
                  )
              );
            }

            return;
          }


          /*
           * OPEN
           */
          setPublicList(
            nextList
          );

          setPublicCursor(
            nextCursor
          );

          setPublicHasMore(
            hasMore
          );


          if (
            direction ===
            "next"
          ) {
            setPublicHistory(
              previous => [
                ...previous,
                currentCursor,
              ]
            );

            setPublicPage(
              previous =>
                previous + 1
            );

          } else {
            setPublicHistory(
              previous =>
                previous.slice(
                  0,
                  -1
                )
            );

            setPublicPage(
              previous =>
                Math.max(
                  1,
                  previous - 1
                )
            );
          }

        } catch (e) {
          setError(
            e?.message ||
            "Failed to load challenges"
          );

        } finally {
          setPageLoading(
            false
          );
        }
      },

      [
        authHeaders,
        pageLoading,

        publicPage,
        publicCursor,
        publicHistory,

        minePage,
        mineCursor,
        mineHistory,
      ]
    );


 
const shareChallenge =
  useCallback(
    async (
      challenge
    ) => {
      if (
        !challenge?.id ||
        !BOT_USERNAME
      ) {
        showErrorModal(
          null,
          "Telegram bot username is not configured."
        );

        return;
      }

      const tg =
        window.Telegram?.WebApp;

      const telegramUserId =
        tg
          ?.initDataUnsafe
          ?.user
          ?.id;

      if (
        !telegramUserId ||
        typeof tg?.shareMessage !==
          "function"
      ) {
        showErrorModal(
          null,
          "Telegram sharing is not available."
        );

        return;
      }

      try {
        const response =
          await fetch(
            `${API_BASE}/api/telegram/prepare-challenge-message`,
            {
              method:
                "POST",

              headers: {
                "Content-Type":
                  "application/json",

                Authorization:
                  `Bearer ${localStorage.getItem("token")}`,
              },

              body:
                JSON.stringify({
                  userId:
                    telegramUserId,

                  challengeId:
                    challenge.id,

                  amount:
                    challenge.amount,

                  currency:
                    challenge.currency,
                }),
            }
          );

        const data =
          await response
            .json()
            .catch(
              () => ({})
            );

        if (
          !response.ok ||
          !data
            ?.preparedMessageId
        ) {
          console.error(
            "prepare-challenge-message failed",
            data
          );

          showErrorModal(
            null,
            "Unable to prepare challenge share."
          );

          return;
        }

        tg.shareMessage(
          data.preparedMessageId,
          success => {
            console.log(
              "challenge shareMessage result:",
              success
            );
          }
        );

      } catch (e) {
        console.error(
          "Challenge share failed",
          e
        );

        showErrorModal(
          null,
          "Unable to share challenge."
        );
      }
    },

    [
      showErrorModal
    ]
  );
  

  const cancelChallenge =
    useCallback(
      async (
        challenge
      ) => {
        if (
          !challenge?.id ||
          busy
        ) {
          return;
        }


        setBusy(
          true
        );

        setError(
          ""
        );


        try {
          const res =
            await fetch(
              `${API_BASE}/api/battle/challenges/${encodeURIComponent(
                challenge.id
              )}/cancel`,

              {
                method:
                  "POST",

                headers:
                  authHeaders(
                    true
                  ),

                body:
                  JSON.stringify(
                    {}
                  ),
              }
            );


          const data =
            await res
              .json()
              .catch(
                () => ({})
              );


          if (
            !res.ok
          ) {
            showErrorModal(
              data?.error,
              "Could not cancel challenge."
            );

            return;
          }


          if (
            data?.user
          ) {
            setUser?.(
              previous => ({
                ...(previous || {}),
                ...data.user,
              })
            );
          }


          showSuccessModal(
            "Your stake was refunded."
          );


          setDetail(
            null
          );

          setView(
            "mine"
          );


          await loadLists();

        } catch (e) {
          showErrorModal(
            null,
            e?.message ||
            "Could not cancel challenge."
          );

        } finally {
          setBusy(
            false
          );
        }
      },

      [
        authHeaders,
        busy,
        loadLists,
        setUser,
        showErrorModal,
        showSuccessModal,
      ]
    );


  const refundChallenge =
    useCallback(
      async (
        challenge
      ) => {
        if (
          !challenge?.id ||
          busy
        ) {
          return;
        }


        setBusy(
          true
        );

        setError(
          ""
        );


        try {
          const res =
            await fetch(
              `${API_BASE}/api/battle/challenges/${encodeURIComponent(
                challenge.id
              )}/refund`,

              {
                method:
                  "POST",

                headers:
                  authHeaders(
                    true
                  ),

                body:
                  JSON.stringify(
                    {}
                  ),
              }
            );


          const data =
            await res
              .json()
              .catch(
                () => ({})
              );


          if (
            !res.ok
          ) {
            showErrorModal(
              data?.error,
              "Could not refund challenge."
            );

            return;
          }


          if (
            data?.user
          ) {
            setUser?.(
              previous => ({
                ...(previous || {}),
                ...data.user,
              })
            );
          }


          const refundCurrency =
            data?.currency ||
            challenge
              ?.currency ||
            "sparks";


          const refundAmount =
            Number(
              data?.refunded ??
              challenge
                ?.amount ??
              0
            );


          showSuccessModal(
            `Refunded ${fmt(
              refundAmount
            )} ${
              CURRENCY[
                refundCurrency
              ]?.label ||
              ""
            }.`
          );


          setDetail(
            null
          );

          setView(
            "mine"
          );


          await loadLists();

        } catch (e) {
          showErrorModal(
            null,
            e?.message ||
            "Could not refund challenge."
          );

        } finally {
          setBusy(
            false
          );
        }
      },

      [
        authHeaders,
        busy,
        loadLists,
        setUser,
        showErrorModal,
        showSuccessModal,
      ]
    );


  const chooseCurrency =
    useCallback(
      (
        nextCurrency
      ) => {
        if (
          nextCurrency !==
            "sparks" &&
          nextCurrency !==
            "runes"
        ) {
          return;
        }


        setCurrency(
          nextCurrency
        );


        setAmount(
          nextCurrency ===
            "runes"
            ? "10"
            : "10000"
        );


        setError(
          ""
        );
      },

      []
    );


  const chooseStake =
    useCallback(
      (
        value
      ) => {
        const clean =
          Math.max(
            0,

            Math.floor(
              Number(
                value || 0
              )
            )
          );


        setAmount(
          String(
            clean
          )
        );


        setError(
          ""
        );
      },

      []
    );


  const handleStakeInput =
    useCallback(
      (
        event
      ) => {
        let value =
          String(
            event
              .target
              .value ||
            ""
          )
            .replace(
              /[^0-9]/g,
              ""
            );


        if (
          value.length >
          10
        ) {
          value =
            value.slice(
              0,
              10
            );
        }


        setAmount(
          value
        );


        setError(
          ""
        );
      },

      []
    );


  const createChallenge =
    useCallback(
      async () => {
        if (
          busy
        ) {
          return;
        }


        const cardId =
          Number(
            createCard
              ?.id
          );


        const stake =
          Math.floor(
            Number(
              amount
            )
          );


        const limits =
          STAKE_LIMITS[
            currency
          ];


        if (
          !Number.isFinite(
            cardId
          )
        ) {
          showErrorModal(
            null,
            "Select a fighter."
          );

          return;
        }


        if (
          !Number.isFinite(
            stake
          ) ||
          stake <
            limits.min ||
          stake >
            limits.max
        ) {
          showErrorModal(
            null,
            `Stake must be between ${fmt(
              limits.min
            )} and ${fmt(
              limits.max
            )} ${CURRENCY[
              currency
            ].label}.`
          );

          return;
        }


        const available =
          Number(
            user?.[
              currency
            ] || 0
          );


        if (
          available <
          stake
        ) {
          showErrorModal(
            null,
            `You don't have enough ${CURRENCY[
              currency
            ].label}.`
          );

          return;
        }


        setBusy(
          true
        );

        setError(
          ""
        );


        try {
          const res =
            await fetch(
              `${API_BASE}/api/battle/challenges`,

              {
                method:
                  "POST",

                headers:
                  authHeaders(
                    true
                  ),

                body:
                  JSON.stringify({
                    cardId,

                    currency,

                    amount:
                      stake,

                    visibility,
                  }),
              }
            );


          const data =
            await res
              .json()
              .catch(
                () => ({})
              );


          if (
            !res.ok
          ) {
            showErrorModal(
              data?.error,
              "Could not create challenge."
            );

            return;
          }


          if (
            data?.user
          ) {
            setUser?.(
              previous => ({
                ...(previous || {}),
                ...data.user,
              })
            );
          }


          await loadLists();

if (data?.challenge?.id) {
  await loadDetail(
    data.challenge.id
  );
}

showSuccessModal(
  "Your stake is locked."
);

        } catch (error) {
          console.error(
            "[Challenge/create]",
            error
          );


          showErrorModal(
            null,
            error?.message ||
            "Could not create challenge."
          );

        } finally {
          setBusy(
            false
          );
        }
      },

      [
  amount,
  authHeaders,
  busy,
  createCard,
  currency,
  loadLists,
  loadDetail,
  setUser,
  showErrorModal,
  showSuccessModal,
  user,
  visibility,
]
    );


  const beginAccept =
    useCallback(
      (
        challenge
      ) => {
        if (
          isChallengeExpired(
            challenge
          )
        ) {
          showErrorModal(
            "CHALLENGE_EXPIRED",
            "This challenge has expired."
          );

          return;
        }


        setAcceptChallenge(
          challenge
        );


        setPickerMode(
          "accept"
        );


        setError(
          ""
        );
      },

      [
        showErrorModal
      ]
    );


  const acceptWithSelectedCard =
    useCallback(
      async (
        selectedOverride = null,
        challengeOverride = null
      ) => {
        const challenge =
          challengeOverride ||
          acceptChallenge;


        if (
          !challenge ||
          busy
        ) {
          return;
        }


        if (
          isChallengeExpired(
            challenge
          )
        ) {
          showErrorModal(
            "CHALLENGE_EXPIRED",
            "This challenge has expired."
          );

          setPickerMode(
            null
          );

          setAcceptChallenge(
            null
          );

          return;
        }


        const selected =
          selectedOverride ||
          readSelectedCardSnapshot();


        const cardId =
          Number(
            selected?.id ||
            localStorage.getItem(
              "selectedCardId"
            )
          );


        if (
          !Number.isFinite(
            cardId
          )
        ) {
          showErrorModal(
            null,
            "Choose a fighter first."
          );

          return;
        }


        const requiredRarity =
          String(
            challenge
              ?.creator
              ?.card
              ?.rarity ||
            ""
          )
            .toLowerCase();


        const chosenRarity =
          String(
            selected
              ?.rarity ||
            ""
          )
            .toLowerCase();


        if (
          requiredRarity &&
          chosenRarity &&
          requiredRarity !==
            chosenRarity
        ) {
          showErrorModal(
            "WRONG_RARITY",
            `This challenge requires a ${challenge.creator.card.rarity} fighter.`
          );

          return;
        }


        setBusy(
          true
        );

        setError(
          ""
        );


        try {
          const res =
            await fetch(
              `${API_BASE}/api/battle/challenges/${encodeURIComponent(
                challenge.id
              )}/accept`,

              {
                method:
                  "POST",

                headers:
                  authHeaders(
                    true
                  ),

                body:
                  JSON.stringify({
                    cardId,
                  }),
              }
            );


          const data =
            await res
              .json()
              .catch(
                () => ({})
              );


          if (
            !res.ok
          ) {
            showErrorModal(
              data?.error,
              "Could not accept challenge."
            );

            return;
          }


          if (
            data?.user
          ) {
            setUser?.(
              previous => ({
                ...(previous || {}),
                ...data.user,
              })
            );
          }


          setPickerMode(
            null
          );


          setAcceptChallenge(
            null
          );


          onNavigate?.(
            "battle",

            {
              battleId:
                data.battleId,

              cardId,

              challengeId:
                challenge.id,

              initialOpponent:
                data.opponent,

              initialPlaySig:
                data.playSig,

              returnTo:
                "challenges",

              returnParams: {
                initialChallengeId:
                  challenge.id,
              },

              openedVia:
                "challenge",
            }
          );

        } catch (e) {
          showErrorModal(
            null,
            e?.message ||
            "Could not accept challenge."
          );

        } finally {
          setBusy(
            false
          );
        }
      },

            [
        acceptChallenge,
        authHeaders,
        busy,
        onNavigate,
        setUser,
        showErrorModal,
      ]
    );


  const handleChallengePickerSelected =
    useCallback(
      (
        params = {}
      ) => {
        const picked =
          params?.card ||
          readSelectedCardSnapshot();


        if (
          params?.cardId
        ) {
          localStorage.setItem(
            "selectedCardId",
            String(
              params.cardId
            )
          );
        }


        /*
         * CREATE CHALLENGE
         */
        if (
          pickerMode ===
          "create"
        ) {
          if (
            !picked?.id
          ) {
            showErrorModal(
              null,
              "Could not read the selected fighter. Please select it again."
            );

            return;
          }


          setCreateCard(
            picked
          );


          setPickerMode(
            null
          );


          return;
        }


        /*
         * ACCEPT CHALLENGE
         */
        if (
          pickerMode ===
          "accept"
        ) {
          const challenge =
            acceptChallenge;


          if (
            !picked?.id
          ) {
            showErrorModal(
              null,
              "Could not read the selected fighter. Please select it again."
            );

            return;
          }


          if (
            !challenge
          ) {
            setPickerMode(
              null
            );

            showErrorModal(
              "CHALLENGE_NOT_FOUND",
              "Challenge is no longer available."
            );

            return;
          }


          if (
            isChallengeExpired(
              challenge
            )
          ) {
            setPickerMode(
              null
            );

            setAcceptChallenge(
              null
            );

            showErrorModal(
              "CHALLENGE_EXPIRED",
              "This challenge has expired."
            );

            return;
          }


          const requiredRarity =
            String(
              challenge
                ?.creator
                ?.card
                ?.rarity ||
              ""
            )
              .trim()
              .toLowerCase();


          const chosenRarity =
            String(
              picked
                ?.rarity ||
              ""
            )
              .trim()
              .toLowerCase();


          if (
            requiredRarity &&
            chosenRarity &&
            requiredRarity !==
              chosenRarity
          ) {
            showErrorModal(
              "WRONG_RARITY",
              `This challenge requires a ${challenge.creator.card.rarity} fighter.`
            );

            return;
          }


          setPickerMode(
            null
          );


          acceptWithSelectedCard(
            picked,
            challenge
          );
        }
      },

      [
        pickerMode,
        acceptChallenge,
        showErrorModal,
        acceptWithSelectedCard,
      ]
    );


  const openChallenge =
    useCallback(
      (
        challenge
      ) => {
        if (
          !challenge?.id
        ) {
          return;
        }


        setDetail(
          challenge
        );


        setView(
          "detail"
        );


        setError(
          ""
        );
      },

      []
    );


  const currentBalance =
    Number(
      user?.[
        currency
      ] || 0
    );


  const parsedStake =
    Math.floor(
      Number(
        amount
      )
    );


  const stakeLimits =
    STAKE_LIMITS[
      currency
    ];


  const stakeValid =
    Number.isFinite(
      parsedStake
    ) &&
    parsedStake >=
      stakeLimits.min &&
    parsedStake <=
      stakeLimits.max;


  const hasEnoughBalance =
    stakeValid &&
    currentBalance >=
      parsedStake;


  const canCreateChallenge =
    !!createCard &&
    stakeValid &&
    hasEnoughBalance &&
    !busy;


  const listMultiplier =
    Number(
      publicList?.[0]
        ?.normalMultiplier
    ) ||
    Number(
      mineList?.[0]
        ?.normalMultiplier
    ) ||
    0;


  const normalMultiplier =
    Number(
      detail
        ?.normalMultiplier
    ) ||
    listMultiplier ||
    DEFAULT_NORMAL_MULTIPLIER;


  const totalEscrow =
    stakeValid
      ? parsedStake * 2
      : 0;


  const winnerPayout =
    stakeValid
      ? Math.min(
          totalEscrow,

          Math.floor(
            parsedStake *
            normalMultiplier
          )
        )
      : 0;


  /*
   * DETAIL
   */
  const detailStake =
    Math.max(
      0,

      Math.floor(
        Number(
          detail?.amount ||
          0
        )
      )
    );


  const detailRawMultiplier =
    Number(
      detail
        ?.normalMultiplier
    );


  const detailMultiplier =
    (
      Number.isFinite(
        detailRawMultiplier
      ) &&
      detailRawMultiplier > 1
    )
      ? Math.min(
          2,
          detailRawMultiplier
        )
      : (
          listMultiplier > 1
            ? listMultiplier
            : normalMultiplier > 1
              ? normalMultiplier
              : DEFAULT_NORMAL_MULTIPLIER
        );


  const detailWinnerPayout =
    detail
      ? getChallengeWinnerPayout(
          {
            ...detail,

            normalMultiplier:
              detailMultiplier,
          }
        )
      : 0;


  const detailNormalWinnerPayout =
    Math.max(
      0,
      Math.floor(
        Number(
          detail
            ?.normalWinnerPayout ||
          detailWinnerPayout ||
          0
        )
      )
    );


  const detailBattlePassWinnerPayout =
    Math.max(
      0,
      Math.floor(
        Number(
          detail
            ?.battlePassWinnerPayout ||
          detailNormalWinnerPayout ||
          0
        )
      )
    );


  const detailHasBattlePassBonus =
    detailBattlePassWinnerPayout >
    detailNormalWinnerPayout;


  const detailTotalEscrow =
    detail
      ? (
          Number(
            detail
              ?.totalEscrow
          ) ||
          detailStake * 2
        )
      : 0;


  const detailExpired =
    detail
      ? isChallengeExpired(
          detail,
          now
        )
      : false;


  const detailAcceptedAt =
  new Date(
    detail?.acceptedAt || 0
  ).getTime();

const detailAcceptedRefundReady =
  detail?.status ===
    "accepted" &&
  Number.isFinite(
    detailAcceptedAt
  ) &&
  now - detailAcceptedAt >=
    CHALLENGE_ACCEPTED_REFUND_MS;

const detailCanRefund =
  !!detail?.isCreator &&
  (
    (
      detail?.status ===
        "open" &&
      detailExpired
    ) ||
    detailAcceptedRefundReady
  );


  const detailResult =
    getChallengeResult(
      detail
    );


  const detailWinnerName =
    getChallengeWinnerName(
      detail
    );


  if (
    loading &&
    !detail &&
    !publicList.length &&
    !mineList.length
  ) {
    return (
      <LoadingSpinner />
    );
  }


  return (
    <div
      className="shop-container"

      style={{
        width:
          "100%",

        maxWidth:
          560,

        margin:
          "0 auto",

        padding:
          "0 10px 0px",

        boxSizing:
          "border-box",
      }}
    >

      {/* ================================================= */}
      {/* TITLE */}
      {/* ================================================= */}

      <div
        style={{
          textAlign:
            "center",

          
        }}
      >
        <div
          className="shadow2" style={{marginBottom: 5,fontSize: 32, fontWeight: 600, color: "#fff"}}
        >
          Battle Challenges
        </div>


        <div
          className="shadow1"

          style={{
            marginBottom: 15,
            color:
              "#fff",

            fontSize:
              14,

            fontWeight: 600
          }}
        >
          Pick your fighter. Set the stake. Take the challenge.
        </div>
      </div>


      {/* ================================================= */}
      {/* TABS */}
      {/* ================================================= */}

      <div
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    width: "100%",
    gap: 6,
    alignItems: "stretch",
    marginTop: 10,
    marginBottom: 12,
  }}
>
  {[
    {
      key: "browse",
      label: "CHALLENGES",
    },
    {
      key: "mine",
      label: "MY DUELS",
    },
    {
      key: "create",
      label: "+ CREATE",
    },
  ].map((tab) => {
    const isActive =
      view === tab.key;

    return (
      <button
        key={tab.key}
        type="button"

        onClick={() => {
          setView(
            tab.key
          );

          setDetail(
            null
          );

          setError(
            ""
          );

          if (
            tab.key !==
            "create"
          ) {
            loadLists();
          }
        }}

        style={{
          width: "100%",
          minWidth: 0,
          height: 46,
          padding: "0 3px",
          margin: 0,
          boxSizing:
            "border-box",

   background:
  tab.key === "create"
    ? isActive
      ? "linear-gradient(180deg, #FFE96A 0%, #FFC52F 46%, #E7970D 100%)"
      : "linear-gradient(180deg, #FFD94F 0%, #F6B51E 48%, #D98208 100%)"
    : isActive
      ? "rgba(112, 61, 27, 0.96)"
      : "rgba(95, 49, 18, 0.76)",

border:
  tab.key === "create"
    ? "2px solid #FFF09A"
    : isActive
      ? "2px solid rgba(135, 78, 36, 0.98)"
      : "2px solid rgba(70, 32, 10, 0.95)",

boxShadow:
  tab.key === "create"
    ? isActive
      ? `
          inset 0 1px 0 rgba(255,255,255,0.45),
          inset 0 -2px 0 rgba(147,82,0,0.40),
          0 2px 0 rgba(90,45,8,0.35),
          0 0 10px rgba(255,196,30,0.35)
        `
      : `
          inset 0 1px 0 rgba(255,255,255,0.35),
          inset 0 -2px 0 rgba(135,69,0,0.38),
          0 2px 0 rgba(90,45,8,0.30)
        `
    : isActive
      ? "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.24), 0 2px 0 rgba(0,0,0,0.24)"
      : "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.18), 0 2px 0 rgba(0,0,0,0.18)",

color:
  tab.key === "create"
    ? "#4B2500"
    : "#fff",

textShadow:
  tab.key === "create"
    ? "0 1px 0 rgba(255,255,255,0.65)"
    : "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 1px 1px rgba(0,0,0,.75)",

          borderRadius: 7,

         

          color: "#fff",

          fontSize:
            tab.key === "mine"
              ? 11
              : 12,

          lineHeight: "42px",
          fontWeight: 600,

          textAlign: "center",
          textTransform:
            "uppercase",

          textShadow:
            "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 1px 1px rgba(0,0,0,.75)",

          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow:
            "ellipsis",

          cursor: "pointer",

          appearance: "none",
          WebkitAppearance:
            "none",

          opacity:
            isActive
              ? 1
              : 0.88,

          fontFamily:
            '"Fredoka", sans-serif',
        }}
      >
        {tab.label}
      </button>
    );
  })}
</div>


      {/* ================================================= */}
      {/* LOAD ERRORS */}
      {/* ================================================= */}

      {!!error && (
        <div
          style={{
            padding:
              "8px 10px",

            margin:
              "8px 0",

            borderRadius:
              8,

            background:
              "rgba(120,0,0,.4)",

            border:
              "1px solid rgba(255,100,100,.35)",

            color:
              "#ffb3ab",

            textAlign:
              "center",

            fontWeight:
              700,

            fontSize:
              13,

            textShadow:
              "1px 1px #000",
          }}
        >
          {error}
        </div>
      )}


      {/* ================================================= */}
      {/* OPEN CHALLENGES */}
      {/* ================================================= */}

      {view ===
        "browse" && (
        <Section
          title={
            `Open Challenges (${
              publicList.filter(
                challenge =>
                  challenge.status ===
                    "open" &&
                  !isChallengeExpired(
                    challenge,
                    now
                  )
              ).length
            })`
          }
        >
          {publicList.filter(
            challenge =>
              challenge.status ===
                "open" &&
              !isChallengeExpired(
                challenge,
                now
              )
          ).length
            ? publicList
                .filter(
                  challenge =>
                    challenge.status ===
                      "open" &&
                    !isChallengeExpired(
                      challenge,
                      now
                    )
                )
                .map(
                  challenge => (
                    <ChallengeRow
                      key={
                        challenge.id
                      }

                      challenge={
                        challenge
                      }

                      now={
                        now
                      }

                      onAccept={
                        beginAccept
                      }

                      onShare={
                        shareChallenge
                      }

                      onCancel={
                        cancelChallenge
                      }

                      onRefund={
                        refundChallenge
                      }

                      onOpen={
                        openChallenge
                      }
                    />
                  )
                )
            : (
              <Empty>
                <strong>No open challenges right now.<br/>Create the first one.</strong>
              </Empty>
            )
          }


          <Pagination
            page={
              publicPage
            }

            hasMore={
              publicHasMore
            }

            loading={
              pageLoading
            }

            onPrevious={() =>
              loadChallengePage(
                "public",
                "prev"
              )
            }

            onNext={() =>
              loadChallengePage(
                "public",
                "next"
              )
            }
          />
        </Section>
      )}


      {/* ================================================= */}
      {/* MY CHALLENGES */}
      {/* ================================================= */}

      {view ===
        "mine" && (
        <Section
          title={
            `My Challenges (${mineList.length})`
          }
        >
          {mineList.length
            ? mineList.map(
                challenge => (
                  <ChallengeRow
                    key={
                      challenge.id
                    }

                    challenge={
                      challenge
                    }

                    now={
                      now
                    }

                    onAccept={
                      beginAccept
                    }

                    onShare={
                      shareChallenge
                    }

                    onCancel={
                      cancelChallenge
                    }

                    onRefund={
                      refundChallenge
                    }

                    onOpen={
                      openChallenge
                    }
                  />
                )
              )
            : (
              <Empty>
                You have not created or accepted any challenges yet.
              </Empty>
            )
          }


          <Pagination
            page={
              minePage
            }

            hasMore={
              mineHasMore
            }

            loading={
              pageLoading
            }

            onPrevious={() =>
              loadChallengePage(
                "mine",
                "prev"
              )
            }

            onNext={() =>
              loadChallengePage(
                "mine",
                "next"
              )
            }
          />
        </Section>
      )}


      {/* ================================================= */}
      {/* CREATE */}
      {/* ================================================= */}

      {view ===
        "create" && (
        <Section
          title=
            "Create Challenge"
        >
          <div
            style={{
              padding:
                "2px 0 5px",
            }}
          >

            {/* FIGHTER */}

            <div
              style={{
                marginBottom:
                  14,
              }}
            >
              <div
              className="shadow1"
                style={{
                  marginBottom:
                    7,

                  color:
                    "#fff",

                  fontSize:
                    17,

                  fontWeight:
                    800,

                  textAlign:
                    "left",

                
                  opacity:
                    0.82,
                }}
              >
                Your Fighter
              </div>


              <button
                type="button"

                onClick={() =>
                  setPickerMode(
                    "create"
                  )
                }

                style={{
                  width:
                    "100%",

                  minHeight:
                    92,

                  display:
                    "flex",

                  alignItems:
                    "center",

                  gap:
                    12,

                  padding:
                    "10px 12px",

                  boxSizing:
                    "border-box",

                  borderRadius:
                    10,

                  border:
                    "2px solid rgba(224,164,77,.6)",

                  background:
                    "linear-gradient(180deg, rgba(67,24,10,.72), rgba(32,6,3,.78))",

                  color:
                    "#fff",

                  font:
                    "inherit",

                  textAlign:
                    "left",

                  cursor:
                    "pointer",
                }}
              >
                {createCard ? (
                  <FighterThumb
                    card={
                      createCard
                    }

                    size={
                      70
                    }
                  />
                ) : (
                  <div
                    style={{
                      width:
                        70,

                      height:
                        70,

                      flex:
                        "0 0 70px",

                      display:
                        "flex",

                      alignItems:
                        "center",

                      justifyContent:
                        "center",

                      borderRadius:
                        10,

                      background:
                        "rgba(0,0,0,.42)",

                      border:
                        "1px solid rgba(255,255,255,.1)",

                      fontSize:
                        30,
                    }}
                  >
                    ⚔️
                  </div>
                )}


                <div
                  style={{
                    flex:
                      1,

                    minWidth:
                      0,
                  }}
                >
                  <div
                    className="shadow1"

                    style={{
                      fontSize:
                        16,

                      lineHeight:
                        "20px",

                      fontWeight:
                        600,

                      whiteSpace:
                        "nowrap",

                      overflow:
                        "hidden",

                      textOverflow:
                        "ellipsis",
                    }}
                  >
                    {createCard
                      ?.name ||
                      "Choose Fighter"
                    }
                  </div>


                  <div
                    style={{
                      marginTop:
                        3,

                      color:
                        "rgba(255,255,255,.7)",

                      fontSize:
                        12,
                    }}
                  >
                    {createCard
                      ? `${createCard.rarity || "Common"} fighter`
                      : "Select the card you want to put into battle."
                    }
                  </div>
                </div>


                <div
                  className=
                    "game-button-small yellow-button"

                  style={{
                    flex:
                      "0 0 auto",

                    margin:
                      0,

                    pointerEvents:
                      "none",
                  }}
                >
                  {createCard
                    ? "CHANGE"
                    : "SELECT"
                  }
                </div>
              </button>
            </div>


            {/* STAKE */}

            <div
  style={{
    width: "100%",
    minWidth: 0,

    marginTop: 14,

    padding: 12,

    boxSizing: "border-box",

    borderRadius: 10,

    background:
      "rgba(30,5,2,.38)",

    border:
      "1px solid rgba(255,255,255,.08)",
  }}
>
              <div
                style={{
                  display:
                    "flex",

                  alignItems:
                    "center",

                  justifyContent:
                    "space-between",

                  gap:
                    10,

                  marginBottom:
                    9,
                }}
              >
                <div
                  style={{
                    color:
                      "#fff",

                    fontSize:
                      16,

                    fontWeight:
                      800,
                  }}
                >
                  Stake
                </div>


                <div
                  style={{
                    display:
                      "flex",

                    alignItems:
                      "center",

                    gap:
                      5,

                    color:
                      "rgba(255,255,255,.7)",

                    fontSize:
                      12,

                    textAlign:
                      "right",
                  }}
                >
                  <span>
                    Balance:
                  </span>


                  <img
                    src={
                      CURRENCY[
                        currency
                      ].icon
                    }

                    alt=""

                    style={{
                      width:
                        17,

                      height:
                        17,

                      objectFit:
                        "contain",
                    }}
                  />


                  <strong
                    style={{
                      color:
                        "#fff",
                    }}
                  >
                    {fmt(
                      currentBalance
                    )}
                  </strong>
                </div>
              </div>


              {/* CURRENCY */}

              <div
  style={{
    display: "grid",

    gridTemplateColumns:
      "repeat(2, minmax(0, 1fr))",

    gap: 7,

    width: "100%",
    minWidth: 0,
  }}
>
                {[
                  "sparks",
                  "runes",
                ].map(
                  cur => {
                    const active =
                      currency ===
                      cur;

                    return (
                      <button
                        key={
                          cur
                        }

                        type="button"

                        onClick={() =>
                          chooseCurrency(
                            cur
                          )
                        }

                        style={{
  width: "100%",
  minWidth: 0,
  height: 48,

  margin: 0,
  padding: "5px 8px",

  boxSizing: "border-box",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  gap: 7,

  borderRadius: 8,

  border:
    active
      ? "2px solid #ffe86b"
      : "2px solid rgba(255,255,255,.12)",

  background:
    active
      ? "linear-gradient(180deg,#a7651d,#6e350f)"
      : "rgba(0,0,0,.28)",

  boxShadow:
    active
      ? "inset 0 0 12px rgba(255,223,76,.25), 0 0 0 1px rgba(255,217,67,.15)"
      : "none",

  color:
    active
      ? "#fff"
      : "rgba(255,255,255,.62)",

  font: "inherit",
  fontSize: 14,
  fontWeight: 900,

  lineHeight: 1,

  cursor: "pointer",

  appearance: "none",
  WebkitAppearance: "none",

  outline: "none",

  WebkitTapHighlightColor:
    "transparent",
}}
                      >
                        <img
                          src={
                            CURRENCY[
                              cur
                            ].icon
                          }

                          alt=""

                          style={{
                            width:
                              23,

                            height:
                              23,

                            objectFit:
                              "contain",
                          }}
                        />


                        {CURRENCY[
                          cur
                        ].label.toUpperCase()}


                        {active && (
                          <span
                            style={{
                              color:
                                "#ffed7c",

                              fontSize:
                                14,
                            }}
                          >
                            ✓
                          </span>
                        )}
                      </button>
                    );
                  }
                )}
              </div>


              {/* PRESETS */}

             <div
  style={{
    display: "grid",

    gridTemplateColumns:
      "repeat(3, minmax(0, 1fr))",

    gap: 6,

    width: "100%",
    minWidth: 0,

    marginTop: 10,
  }}
>
                {STAKE_OPTIONS[
                  currency
                ].map(
                  value => {
                    const selected =
                      parsedStake ===
                      value;

                    return (
                      <button
                        key={
                          `${currency}-${value}`
                        }

                        type="button"

                        onClick={() =>
                          chooseStake(
                            value
                          )
                        }

                        style={{
  width: "100%",
  minWidth: 0,
  height: 38,

  margin: 0,
  padding: "0 5px",

  boxSizing: "border-box",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  borderRadius: 7,

  border:
    selected
      ? "2px solid #ffe667"
      : "2px solid rgba(255,255,255,.12)",

  background:
    selected
      ? "linear-gradient(180deg,#9c581a,#60300e)"
      : "rgba(0,0,0,.26)",

  color:
    selected
      ? "#fff"
      : "rgba(255,255,255,.72)",

  font: "inherit",
  fontSize: 13,
  fontWeight: 800,

  lineHeight: 1,

  cursor: "pointer",

  appearance: "none",
  WebkitAppearance: "none",

  outline: "none",

  WebkitTapHighlightColor:
    "transparent",
}}
                      >
                        {fmt(
                          value
                        )}
                      </button>
                    );
                  }
                )}
              </div>


              {false && (
  <>
    {/* CUSTOM */}

    <div
      style={{
        marginTop: 11,
        color: "rgba(255,255,255,.58)",
        fontSize: 11,
        fontWeight: 700,
        textAlign: "left",
        textTransform: "uppercase",
      }}
    >
      Custom Amount
    </div>

    <div
      style={{
        position: "relative",
        marginTop: 5,
      }}
    >
      <img
        src={CURRENCY[currency].icon}
        alt=""
        style={{
          position: "absolute",
          left: 12,
          top: "50%",
          width: 25,
          height: 25,
          objectFit: "contain",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          zIndex: 2,
        }}
      />

      <input
        type="text"
        inputMode="numeric"
        value={amount}
        onChange={handleStakeInput}
        placeholder={String(stakeLimits.min)}
        autoComplete="off"
        style={{
          width: "100%",
          height: 54,
          padding: "0 15px 0 46px",
          boxSizing: "border-box",
          borderRadius: 8,
          border: stakeValid
            ? "2px solid rgba(224,164,77,.65)"
            : "2px solid rgba(255,90,90,.72)",
          outline: "none",
          background: "rgba(0,0,0,.48)",
          color: "#fff",
          fontSize: 22,
          fontWeight: 800,
          fontFamily: "inherit",
        }}
      />
    </div>

    {!stakeValid && (
      <div
        style={{
          marginTop: 6,
          color: "#ff9a8d",
          fontSize: 12,
          textAlign: "left",
        }}
      >
        Minimum {fmt(stakeLimits.min)} and maximum {fmt(stakeLimits.max)}{" "}
        {CURRENCY[currency].label}.
      </div>
    )}

    {stakeValid && !hasEnoughBalance && (
      <div
        style={{
          marginTop: 6,
          color: "#ff9a8d",
          fontSize: 12,
          textAlign: "left",
        }}
      >
        You don't have enough {CURRENCY[currency].label}.
      </div>
    )}
  </>
)}

              <div
  style={{
    display: "grid",

    gridTemplateColumns:
      "repeat(2, minmax(0, 1fr))",

    gap: 7,

    width: "100%",
    minWidth: 0,

    marginTop: 10,
  }}
>
               <div
  style={{
    minWidth: 0,

    padding: "9px",

    boxSizing: "border-box",

    borderRadius: 7,

    background:
      "rgba(0,0,0,.3)",

    border:
      "1px solid rgba(255,255,255,.06)",

    textAlign: "left",
  }}
>
                  <div
                    style={{
                      color:
                        "rgba(255,255,255,.52)",

                      fontSize:
                        10,

                      fontWeight:
                        600,

                      marginBottom:
                        3,
                    }}
                  >
                    YOU LOCK
                  </div>


                  <CurrencyAmount
                    currency={
                      currency
                    }

                    amount={
                      stakeValid
                        ? parsedStake
                        : 0
                    }
                  />
                </div>


               <div
  style={{
    minWidth: 0,

    padding: "9px",

    boxSizing: "border-box",

    borderRadius: 7,

    background:
      "rgba(0,0,0,.3)",

    border:
      "1px solid rgba(255,255,255,.06)",

    textAlign: "left",
  }}
>
                  <div
                    style={{
                      color:
                        "rgba(255,255,255,.52)",

                      fontSize:
                        10,

                      fontWeight:
                        600,

                      marginBottom:
                        3,
                    }}
                  >
                    WINNER RECEIVES
                  </div>


                  <CurrencyAmount
                    currency={
                      currency
                    }

                    amount={
                      winnerPayout
                    }
                  />
                </div>
              </div>
            </div>


            {/* VISIBILITY */}

            <div
              style={{
                marginTop:
                  14,
              }}
            >
              <div className="shadow1"
                style={{
                  color:
                    "#fff",

                  fontSize:
                    16,

                  fontWeight:
                    600,

                  textAlign:
                    "left",

                  marginBottom:
                    7,
                }}
              >
                Who Can Accept?
              </div>


              <div
                style={{
                  display:
                    "grid",

                  gridTemplateColumns:
                    "1fr 1fr",

                  gap:
                    7,
                }}
              >
                {[
                  {
                    value:
                      "public",

                    title:
                      "PUBLIC",

                    text:
                      "Visible to everyone",
                  },

                  {
                    value:
                      "private",

                    title:
                      "SHARE ONLY",

                    text:
                      "Only through your link",
                  },
                ].map(
                  option => {
                    const active =
                      visibility ===
                      option.value;

                    return (
                      <button
                        key={
                          option.value
                        }

                        type="button"

                        onClick={() =>
                          setVisibility(
                            option.value
                          )
                        }

                        style={{
                          minHeight:
                            62,

                          padding:
                            "7px 8px",

                          borderRadius:
                            8,

                          border:
                            active
                              ? "2px solid #8bdb4e"
                              : "2px solid rgba(255,255,255,.11)",

                          background:
                            active
                              ? "linear-gradient(180deg,rgba(77,129,30,.72),rgba(35,70,13,.72))"
                              : "rgba(0,0,0,.26)",

                          color:
                            "#fff",

                          font:
                            "inherit",

                          cursor:
                            "pointer",
                        }}
                      >
                        <div
                          style={{
                            fontSize:
                              13,

                            fontWeight:
                              900,
                          }}
                        >
                          {active
                            ? "✓ "
                            : ""
                          }

                          {option.title}
                        </div>


                        <div
                          style={{
                            marginTop:
                              3,

                            color:
                              "rgba(255,255,255,.62)",

                            fontSize:
                              11,

                            lineHeight:
                              "12px",
                          }}
                        >
                          {option.text}
                        </div>
                      </button>
                    );
                  }
                )}
              </div>
            </div>


            {/* INFO */}

            <div
              style={{
                marginTop:
                  14,

                padding:
                  "10px 11px",

                borderRadius:
                  8,

                background:
                  "rgba(0,0,0,.22)",

                border:
                  "1px solid rgba(255,255,255,.07)",

                color:
                  "rgba(255,255,255,.68)",

                fontSize:
                  12,

                lineHeight:
                  "16px",

                textAlign:
                  "left",
              }}
            >
              Your stake is locked when the challenge is created.
              The opponent must match the same stake and use a fighter
              of the same rarity. If nobody accepts before expiration,
              you can claim your refund from My Challenges.
            </div>


            {/* CREATE */}

            <button
              type="button"

              className=
                "game-button green-button"

              disabled={
                !canCreateChallenge
              }

              onClick={
                createChallenge
              }

              style={{
                width:
                  "100%",

                minHeight:
                  52,

                marginTop:
                  14,

                opacity:
                  canCreateChallenge
                    ? 1
                    : 0.5,

                filter:
                  canCreateChallenge
                    ? "none"
                    : "grayscale(.35)",
              }}
            >
              {busy
                ? "CREATING..."
                : !createCard
                  ? "SELECT A FIGHTER"
                  : !stakeValid
                    ? "INVALID STAKE"
                    : !hasEnoughBalance
                      ? `NOT ENOUGH ${CURRENCY[currency].label.toUpperCase()}`
                      : `LOCK ${fmt(parsedStake)} ${CURRENCY[currency].label.toUpperCase()} & CREATE`
              }
            </button>
          </div>
        </Section>
      )}


      {/* ================================================= */}
      {/* DETAILS */}
      {/* ================================================= */}

      {view ===
        "detail" &&
        detail && (
          <Section
            title=
              "CHALLENGE DETAILS"
          >
            <div
              style={{
                textAlign:
                  "center",

                color:
                  "#fff",

                paddingBottom:
                  6,
              }}
            >

              {/* FIGHTERS */}

              {detail
                ?.acceptor
                ?.card ? (
                  <div
                    style={{
                      display:
                        "grid",

                      gridTemplateColumns:
                        "1fr auto 1fr",

                      alignItems:
                        "center",

                      gap:
                        10,

                      maxWidth:
                        360,

                      margin:
                        "0 auto 12px",
                    }}
                  >
                    <div
                      style={{
                        minWidth:
                          0,
                      }}
                    >
                      <div
                        style={{
                          display:
                            "flex",

                          justifyContent:
                            "center",
                        }}
                      >
                        <FighterThumb
                          card={
                            detail
                              ?.creator
                              ?.card
                          }

                          size={
                            90
                          }
                        />
                      </div>

                      <div
                        style={{
                          marginTop:
                            6,

                          fontSize:
                            13,

                          fontWeight:
                            800,

                          overflow:
                            "hidden",

                          textOverflow:
                            "ellipsis",

                          whiteSpace:
                            "nowrap",
                        }}
                      >
                        {capitalizeName(
                          detail
                            ?.creator
                            ?.name ||
                          "Creator"
                        )}
                      </div>

                      <div
                        style={{
                          marginTop:
                            3,
                        }}
                      >
                        <RarityBadge
                          rarity={
                            detail
                              ?.creator
                              ?.card
                              ?.rarity
                          }
                        />
                      </div>

                      {detail.status ===
                        "settled" && (
                          <div
                            style={{
                              marginTop:
                                5,

                              fontSize:
                                11,

                              fontWeight:
                                900,

                              color:
                                String(
                                  detail
                                    ?.winnerUserId
                                ) ===
                                String(
                                  detail
                                    ?.creator
                                    ?.userId
                                )
                                  ? "#aaff72"
                                  : "#ff8b7b",
                            }}
                          >
                            {String(
                              detail
                                ?.winnerUserId
                            ) ===
                            String(
                              detail
                                ?.creator
                                ?.userId
                            )
                              ? "WINNER"
                              : "LOST"
                            }
                          </div>
                        )
                      }
                    </div>


                    <div
                      style={{
                        fontSize:
                          22,

                        fontWeight:
                          900,

                        color:
                          "#ffe66a",

                        textShadow:
                          "2px 2px #000",
                      }}
                    >
                      VS
                    </div>


                    <div
                      style={{
                        minWidth:
                          0,
                      }}
                    >
                      <div
                        style={{
                          display:
                            "flex",

                          justifyContent:
                            "center",
                        }}
                      >
                        <FighterThumb
                          card={
                            detail
                              ?.acceptor
                              ?.card
                          }

                          size={
                            90
                          }
                        />
                      </div>

                      <div
                        style={{
                          marginTop:
                            6,

                          fontSize:
                            13,

                          fontWeight:
                            800,

                          overflow:
                            "hidden",

                          textOverflow:
                            "ellipsis",

                          whiteSpace:
                            "nowrap",
                        }}
                      >
                        {capitalizeName(
                          detail
                            ?.acceptor
                            ?.name ||
                          "Opponent"
                        )}
                      </div>

                      <div
                        style={{
                          marginTop:
                            3,
                        }}
                      >
                        <RarityBadge
                          rarity={
                            detail
                              ?.acceptor
                              ?.card
                              ?.rarity
                          }
                        />
                      </div>

                      {detail.status ===
                        "settled" && (
                          <div
                            style={{
                              marginTop:
                                5,

                              fontSize:
                                11,

                              fontWeight:
                                900,

                              color:
                                String(
                                  detail
                                    ?.winnerUserId
                                ) ===
                                String(
                                  detail
                                    ?.acceptor
                                    ?.userId
                                )
                                  ? "#aaff72"
                                  : "#ff8b7b",
                            }}
                          >
                            {String(
                              detail
                                ?.winnerUserId
                            ) ===
                            String(
                              detail
                                ?.acceptor
                                ?.userId
                            )
                              ? "WINNER"
                              : "LOST"
                            }
                          </div>
                        )
                      }
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display:
                          "flex",

                        justifyContent:
                          "center",

                        marginBottom:
                          8,
                      }}
                    >
                      <FighterThumb
                        card={
                          detail
                            ?.creator
                            ?.card
                        }

                        size={
                          104
                        }
                      />
                    </div>


                    <div
                      style={{
                        fontSize:
                          23,

                        fontWeight:
                          900,

                        textShadow:
                          "2px 2px #000",
                      }}
                    >
                      {capitalizeName(
                        detail
                          ?.creator
                          ?.name ||
                        "Player"
                      )}
                    </div>


                    <div
                      style={{
                        display:
                          "flex",

                        alignItems:
                          "center",

                        justifyContent:
                          "center",

                        gap:
                          7,

                        marginTop:
                          5,
                      }}
                    >
                      <span
                        style={{
                          opacity:
                            0.75,
                        }}
                      >
                        {detail
                          ?.creator
                          ?.card
                          ?.name ||
                          "Fighter"
                        }
                      </span>

                      <RarityBadge
                        rarity={
                          detail
                            ?.creator
                            ?.card
                            ?.rarity
                        }
                      />
                    </div>
                  </>
                )
              }


              {/* RESULT */}

              {detailResult && (
                <div
                  style={{
                    margin:
                      "12px auto 0",

                    maxWidth:
                      330,

                    padding:
                      "11px 12px",

                    borderRadius:
                      9,

                    background:
                      detailResult ===
                      "win"
                        ? "rgba(39,120,18,.42)"
                        : "rgba(130,25,18,.42)",

                    border:
                      detailResult ===
                      "win"
                        ? "1px solid rgba(150,255,100,.3)"
                        : "1px solid rgba(255,110,95,.3)",

                    color:
                      detailResult ===
                      "win"
                        ? "#baff8f"
                        : "#ff9b8f",

                    fontSize:
                      22,

                    fontWeight:
                      900,

                    textShadow:
                      "1px 1px #000",
                  }}
                >
                  {detailResult ===
                    "win"
                    ? "🏆 YOU WON"
                    : "💀 YOU LOST"
                  }
                </div>
              )}


              {detail.status ===
                "settled" &&
                detailWinnerName && (
                  <div
                    style={{
                      marginTop:
                        7,

                      color:
                        "rgba(255,255,255,.72)",

                      fontSize:
                        12,
                    }}
                  >
                    Winner:{" "}

                    <strong
                      style={{
                        color:
                          "#fff",
                      }}
                    >
                      {detailWinnerName}
                    </strong>
                  </div>
                )
              }


              {/* STAKE / PAYOUT */}

              <div
                style={{
                  margin:
                    "14px auto 8px",

                  padding:
                    12,

                  borderRadius:
                    12,

                  background:
                    "rgba(0,0,0,.35)",

                  border:
                    "1px solid rgba(255,255,255,.08)",

                  maxWidth:
                    330,
                }}
              >
                <div
                  style={{
                    fontSize:
                      11,

                    opacity:
                      0.65,

                    fontWeight:
                      700,
                  }}
                >
                  STAKE PER PLAYER
                </div>


                <div
                  style={{
                    fontSize:
                      24,

                    marginTop:
                      5,
                  }}
                >
                  <CurrencyAmount
                    currency={
                      detail.currency
                    }

                    amount={
                      detail.amount
                    }
                  />
                </div>


                <div
                  style={{
                    marginTop:
                      11,

                    paddingTop:
                      10,

                    borderTop:
                      "1px solid rgba(255,255,255,.08)",
                  }}
                >
                  <div
                    style={{
                      fontSize:
                        10,

                      opacity:
                        0.62,

                      fontWeight:
                        700,
                    }}
                  >
                    {detail.status ===
                      "settled"
                      ? "WINNER RECEIVED"
                      : "WINNER RECEIVES"
                    }
                  </div>


                  <div
                    style={{
                      fontSize:
                        20,

                      marginTop:
                        4,
                    }}
                  >
                    <CurrencyAmount
                      currency={
                        detail.currency
                      }

                      amount={
                        detail.status ===
                          "settled"
                          ? detailWinnerPayout
                          : detailNormalWinnerPayout
                      }
                    />
                  </div>


                  {detailHasBattlePassBonus && (
                    <div
                      style={{
                        marginTop:
                          10,

                        padding:
                          "9px 10px",

                        borderRadius:
                          8,

                        background:
                          "rgba(255,215,60,.10)",

                        border:
                          "1px solid rgba(255,225,90,.22)",
                      }}
                    >
                      <div
                        style={{
                          fontSize:
                            10,

                          fontWeight:
                            900,

                          color:
                            "#fff",

                          textTransform:
                            "uppercase",
                        }}
                      >
                        WITH BATTLE PASS
                      </div>


                      <div
                        style={{
                          fontSize:
                            18,

                          marginTop:
                            4,
                        }}
                      >
                        <CurrencyAmount
                          currency={
                            detail.currency
                          }

                          amount={
                            detailBattlePassWinnerPayout
                          }
                        />
                      </div>


                      <div
                        style={{
                          marginTop:
                            4,

                          fontSize:
                            11,

                          color:
                            "rgba(255,255,255,.64)",

                          lineHeight:
                            "15px",
                        }}
                      >
                        {hasBattlePass
                          ? "Your Battle Pass bonus will be applied if you win."
                          : (
                            <>
                              Battle Pass increases the payout to{" "}

                              <strong
                                style={{
                                  color:
                                    "#ffe976",
                                }}
                              >
                                {fmt(
                                  detailBattlePassWinnerPayout
                                )}{" "}

                                {
                                  CURRENCY[
                                    detail.currency
                                  ]?.label
                                }
                              </strong>.
                            </>
                          )
                        }
                      </div>


                      {!battlePassLoading &&
                        !hasBattlePass && (
                          <button
                            type="button"

                            className=
                              "game-button-small yellow-button"

                            onClick={() =>
                              onNavigate?.(
                                "battlePass"
                              )
                            }

                            style={{
                              width:
                                "100%",

                              marginTop:
                                8,
                            }}
                          >
                            GET BATTLE PASS
                          </button>
                        )
                      }
                    </div>
                  )}
                </div>
              </div>


              {/* STATUS */}

              <div
                style={{
                  fontSize:
                    14,

                  opacity:
                    0.78,
                }}
              >
                Status:{" "}

                <b>
                  {detail.status ===
                    "open" &&
                    detailExpired
                    ? "EXPIRED"
                    : String(
                        detail.status ||
                        ""
                      ).toUpperCase()
                  }
                </b>


                {detail.status ===
                  "open" &&
                  !detailExpired
                  ? ` · ${timeLeft(
                      detail
                        .expiresAt,
                      now
                    )} left`
                  : ""
                }
              </div>


              {/* REFUND */}

              {detailCanRefund && (
                <div
                  style={{
                    margin:
                      "10px auto 0",

                    padding:
                      "8px 10px",

                    maxWidth:
                      330,

                    borderRadius:
                      8,

                    background:
                      "rgba(99,61,4,.35)",

                    border:
                      "1px solid rgba(255,190,80,.25)",

                    color:
                      "#ffd484",

                    fontSize:
                      12,

                    lineHeight:
                      "16px",
                  }}
                >
                  Nobody accepted this challenge before it expired.
                  Your locked stake is ready to be refunded.
                </div>
              )}


              {/* ACTIONS */}

              <div
                style={{
                  display:
                    "grid",

                  gap:
                    7,

                  marginTop:
                    14,
                }}
              >
                {detail
                  .canAccept &&
                  !detailExpired && (
                    <button
                      type="button" style={{marginBottom: 5}}

                      className=
                        "game-button green-button"

                      onClick={() =>
                        beginAccept(
                          detail
                        )
                      }
                    >
                      ACCEPT CHALLENGE
                    </button>
                  )
                }


                {detailCanRefund && (
                  <button
                    type="button"

                    className=
                      "game-button green-button"

                    onClick={() =>
                      refundChallenge(
                        detail
                      )
                    }

                    disabled={
                      busy
                    }
                  >
                    {busy
                      ? "REFUNDING..."
                      : `REFUND ${fmt(
                          detail.amount
                        )} ${
                          CURRENCY[
                            detail.currency
                          ]?.label?.toUpperCase() ||
                          ""
                        }`
                    }
                  </button>
                )}


                {detail.status ===
                  "open" &&
                  !detailExpired && (
                    <button
                      type="button"

                      className=
                        "game-button yellow-button"

                      onClick={() =>
                        shareChallenge(
                          detail
                        )
                      }
                    >
                      SHARE CHALLENGE
                    </button>
                  )
                }


                {detail
                  .canCancel &&
                  !detailExpired && (
                    <button
                      type="button"  style={{marginTop: 5}}

                      className=
                        "game-button red-button"

                      onClick={() =>
                        cancelChallenge(
                          detail
                        )
                      }

                      disabled={
                        busy
                      }
                    >
                      {busy
                        ? "CANCELLING..."
                        : "CANCEL & REFUND"
                      }
                    </button>
                  )
                }


                <button style={{marginTop: 5, marginBottom: -5}}
                  type="button"

                  className=
                    "game-button green-button"

                  onClick={() => {
                    setDetail(
                      null
                    );

                    setView(
                      "browse"
                    );

                    loadLists();
                  }}
                >
                  BACK TO CHALLENGES
                </button>
              </div>
            </div>
          </Section>
        )
      }


      {/* ================================================= */}
      {/* SUCCESS MODAL */}
      {/* ================================================= */}

      <div>
        {successModalOpen && (
          <div
            className="modal_box"

            style={{
              zIndex:
                10000,
            }}
          >
            <div
              className="modal_title"
            >
              Success

              <button
                type="button"

                className="modal_close"

                onClick={() => {
                  setSuccessModalOpen(
                    false
                  );

                  setSuccessModalMessage(
                    ""
                  );
                }}
              />
            </div>


            <motion.div
              className="modal_body" initial={{
              scale:
                0.8,

              opacity:
                0,
            }}

            animate={{
              scale:
                1,

              opacity:
                1,
            }}

            exit={{
              scale:
                0.92,

              opacity:
                0,
            }}
            >
              <div
                className="modal_inside"
              >
                <img
                  src="/finger.png"

                  alt=""

                  style={{
                    height:
                      80,

                    marginBottom:
                      10,
                  }}
                />


                <div
                  className="shadow2"

                  style={{
                    fontSize:
                      28,

                    fontWeight:
                      600,

                    marginBottom:
                      10,
                  }}
                >
                  Success!
                </div>


                <p
                  className=
                    "info shadow1"

                  style={{
                    fontSize:
                      16,

                    color:
                      "#ddd",

                    lineHeight:
                      "21px",
                  }}
                >
                  {
                    successModalMessage
                  }
                </p>
              </div>


              <div
                className="modal_footer"
              >
                <button
                  type="button"

                  className=
                    "game-button green-button"

                  onClick={() => {
                    setSuccessModalOpen(
                      false
                    );

                    setSuccessModalMessage(
                      ""
                    );
                  }}
                >
                  AWESOME
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>


      {/* ================================================= */}
      {/* ERROR MODAL */}
      {/* ================================================= */}

      <div>
        {errorModalOpen && (
          <div
            className="modal_box"

            style={{
              zIndex:
                10000,
            }}
          >
            <div
              className="modal_title"
            >
              Error

              <button
                type="button"

                className="modal_close"

                onClick={() => {
                  setErrorModalOpen(
                    false
                  );

                  setErrorModalMessage(
                    ""
                  );
                }}
              />
            </div>


            <motion.div
              className="modal_body"  initial={{
              scale:
                0.8,

              opacity:
                0,
            }}

            animate={{
              scale:
                1,

              opacity:
                1,
            }}

            exit={{
              scale:
                0.92,

              opacity:
                0,
            }}
            >
              <div
                className="modal_inside"
              >
                <img
                  src="/finger.png"

                  alt=""

                  style={{
                    height:
                      80,

                    marginBottom:
                      10,

                    transform:
                      "rotate(180deg)",
                  }}
                />


                <div
                  className="shadow2"

                  style={{
                    fontSize:
                      28,

                    fontWeight:
                      600,

                    marginBottom:
                      10,
                  }}
                >
                  Something Wrong!
                </div>


                <p
                  className=
                    "info shadow1"

                  style={{
                    fontSize:
                      18,

                    color:
                      "#ddd",

                    lineHeight:
                      "24px",
                  }}
                >
                  {
                    errorModalMessage
                  }
                </p>
              </div>


              <div
                className="modal_footer"
              >
                <button
                  type="button"

                  className=
                    "game-button red-button"

                  onClick={() => {
                    setErrorModalOpen(
                      false
                    );

                    setErrorModalMessage(
                      ""
                    );
                  }}
                >
                  CLOSE
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>


    {/* ================================================= */}
{/* CARD PICKER */}
{/* ================================================= */}

<ChallengeFightPickerModal
  open={
    !!pickerMode
  }

  user={
    user
  }

  mode={
    pickerMode
  }

  challenge={
    acceptChallenge
  }

  onClose={() => {
    setPickerMode(
      null
    );

    setAcceptChallenge(
      null
    );
  }}

  onSelectDone={
    handleChallengePickerSelected
  }

  onNavigate={
    onNavigate
  }
/>
    </div>
  );
}